import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  login_path,
  privacy_path,
  registration_path,
  signup_path,
  terms_path,
} from '@/rails/routes';
import { Form, Link, router } from '@inertiajs/react';

const CONTACT_EMAIL = 'lance.foley@caprocklabs.com';

const TEXT_LINK =
  'rounded-sm underline decoration-accent underline-offset-4 hover:decoration-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring';

interface RegistrationForm {
  name: string | null;
  emailAddress: string | null;
  companyName: string | null;
}

interface Props {
  open: boolean;
  errors: string[];
  form: RegistrationForm;
}

const New = ({ open, errors, form }: Props) => {
  // The server sends a list of messages. Anything else under this name is not ours to show.
  const messages = Array.isArray(errors) ? errors : [];

  return (
    <>
      {open ? (
        <>
          <h1 className="authority-display text-3xl leading-tight">Create your account</h1>

          {messages.length > 0 ? (
            <div
              role="alert"
              className="border-destructive text-destructive mt-6 border-l-2 pl-3 text-sm"
            >
              <p className="font-medium">The account was not created.</p>
              <ul className="mt-1 list-disc space-y-1 pl-5">
                {messages.map((message) => (
                  <li key={message}>{message}</li>
                ))}
              </ul>
            </div>
          ) : null}

          {/* A refused signup is rendered at the address it was sent to, which is not a
              page of its own. Put this form's address back so a refresh lands here. */}
          <Form
            method="post"
            action={registration_path()}
            onError={() =>
              router.replace({ url: signup_path(), preserveState: true, preserveScroll: true })
            }
            className="mt-6 flex flex-col gap-5"
          >
            {({ processing }) => (
              <>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="name">Your name</Label>
                  <Input
                    id="name"
                    type="text"
                    name="name"
                    autoComplete="name"
                    autoFocus
                    required
                    defaultValue={form.name ?? ''}
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <Label htmlFor="email_address">Email</Label>
                  <Input
                    id="email_address"
                    type="email"
                    name="email_address"
                    autoComplete="email"
                    required
                    defaultValue={form.emailAddress ?? ''}
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    name="password"
                    autoComplete="new-password"
                    required
                    minLength={12}
                    aria-describedby="password_hint"
                  />
                  <p id="password_hint" className="text-muted-foreground text-sm">
                    12 characters or more.
                  </p>
                </div>

                <div className="flex flex-col gap-2">
                  <Label htmlFor="company_name">Business name</Label>
                  <Input
                    id="company_name"
                    type="text"
                    name="company_name"
                    autoComplete="organization"
                    required
                    defaultValue={form.companyName ?? ''}
                  />
                </div>

                <Button type="submit" size="lg" disabled={processing}>
                  Create account
                </Button>
                <p className="text-muted-foreground text-xs leading-relaxed">
                  When you create an account, you agree to the{' '}
                  <Link href={terms_path()} className="underline underline-offset-4">
                    terms of service
                  </Link>
                  . The{' '}
                  <Link href={privacy_path()} className="underline underline-offset-4">
                    privacy policy
                  </Link>{' '}
                  says how we use your data.
                </p>
              </>
            )}
          </Form>
        </>
      ) : (
        <>
          <h1 className="authority-display text-3xl leading-tight">Signup is not open yet</h1>
          <p className="text-muted-foreground mt-3 text-sm leading-relaxed">
            Signup opens after the first client apps are live.
          </p>
          <p className="mt-4 text-sm leading-relaxed">
            To ask for access before then, email{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} className={`break-all ${TEXT_LINK}`}>
              {CONTACT_EMAIL}
            </a>
            .
          </p>
        </>
      )}

      <p className="text-muted-foreground mt-6 text-sm">
        Already have an account?{' '}
        <Link href={login_path()} className={`text-foreground ${TEXT_LINK}`}>
          Log in
        </Link>
      </p>
    </>
  );
};

export default New;
